import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getToken, setToken, meApi, loginApi, signupApi,
  listVideos as apiListVideos, listChannels as apiListChannels,
  toggleLike as apiToggleLike, toggleWatchLater as apiToggleWL,
  toggleSubscribe as apiToggleSub, setPlan as apiSetPlan,
  getAnnouncement, listCategories, getPublicSettings,
} from '../lib/api';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [announcement, setAnnouncement] = useState('');
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ allow_custom_categories: true, allow_signups: true });

  const refreshCategories = useCallback(async () => {
    try { setCategories(await listCategories()); } catch {}
  }, []);
  const refreshSettings = useCallback(async () => {
    try { setSettings(await getPublicSettings()); } catch {}
  }, []);

  // Inject AdSense / third-party head snippet from owner-set settings.
  // Source is admin-controlled (only owner can write `adsense_head` via /api/owner/settings),
  // so this is a trusted snippet — but we still parse it via DOMParser instead of writing
  // a string straight to a live-DOM innerHTML, to avoid the document object being mutated
  // mid-parse.
  useEffect(() => {
    const code = settings?.adsense_head;
    if (!code) return;
    const marker = document.getElementById('vidhub-adsense-head');
    if (marker) marker.remove();
    let parsedDoc;
    try {
      parsedDoc = new DOMParser().parseFromString(`<html><head>${code}</head></html>`, 'text/html');
    } catch { return; }
    const head = parsedDoc?.head;
    if (!head) return;
    const container = document.createElement('div');
    container.id = 'vidhub-adsense-head';
    container.style.display = 'none';
    // Move <script> tags into <head> so they execute (DOMParser-created scripts don't auto-run)
    Array.from(head.querySelectorAll('script')).forEach(old => {
      const s = document.createElement('script');
      for (const { name, value } of Array.from(old.attributes)) s.setAttribute(name, value);
      if (old.textContent) s.textContent = old.textContent;
      document.head.appendChild(s);
    });
    // keep meta tags + link tags
    Array.from(head.querySelectorAll('meta, link')).forEach(n => document.head.appendChild(n.cloneNode(true)));
    document.body.appendChild(container);
  }, [settings?.adsense_head]);

  // Boot: restore session
  useEffect(() => {
    (async () => {
      try {
        if (getToken()) {
          const { user } = await meApi();
          setUser(user);
        }
      } catch { setToken(null); }
      try { const { message } = await getAnnouncement(); setAnnouncement(message || ''); } catch {}
      await Promise.all([refreshCategories(), refreshSettings()]);
      setBooting(false);
    })();
  }, [refreshCategories, refreshSettings]);

  const login = useCallback(async (email, password) => {
    const { token, user } = await loginApi(email, password);
    setToken(token); setUser(user);
    return user;
  }, []);

  const signup = useCallback(async (email, password, name, extras = {}) => {
    const { token, user } = await signupApi(email, password, name, extras);
    setToken(token); setUser(user);
    return user;
  }, []);

  const logout = useCallback(() => { setToken(null); setUser(null); }, []);

  const setPlan = useCallback(async (plan) => {
    const { user } = await apiSetPlan(plan);
    setUser(user);
    return user;
  }, []);

  // Helpers used by components - they just call the API directly, but expose here for convenience
  const value = {
    user, booting, announcement, setAnnouncement,
    categories, refreshCategories,
    settings, refreshSettings,
    login, signup, logout, setPlan,
    refreshMe: async () => { const { user } = await meApi(); setUser(user); },
    // alias helpers so existing components keep working
    toggleLike: (id) => apiToggleLike(id),
    toggleWatchLater: (id) => apiToggleWL(id),
    toggleSub: (id) => apiToggleSub(id),
    listVideos: apiListVideos,
    listChannels: apiListChannels,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
